import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Badge, Button, Form, Modal } from 'react-bootstrap';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import { useJanelaDestino } from '../hooks/useJanelaDestino';
import {
  apenasDigitos,
  diferencas,
  MAX_NOME,
  paraPayload,
  paraRascunho,
  rascunhoVazio,
  textoDestino,
  validarJanela
} from '../utils/janelaDescarga';
import JanelaDestinoSection from './JanelaDestinoSection';
import JanelaSimuladorSection from './JanelaSimuladorSection';
import JanelaTurnosSection from './JanelaTurnosSection';

const destinoCompleto = (rascunho) => (
  rascunho.tipo === 'CNPJ'
    ? apenasDigitos(rascunho.cnpj).length === 14
    : apenasDigitos(rascunho.cnpjRaiz).length === 8
);

const MODOS_FORA_JANELA = [
  {
    valor: 'PROXIMO_TURNO',
    texto: 'Começar a contar no próximo turno',
    ajuda: 'Chegou antes de abrir, entre turnos, depois do horário ou em dia fechado: o tempo de espera começa na próxima abertura.'
  },
  {
    valor: 'CHEGADA',
    texto: 'Contar desde a chegada',
    ajuda: 'Ignora os turnos: o tempo de espera começa no momento em que o veículo chega na loja.'
  }
];

const textoPendencias = (quantidade) => (
  quantidade === 1 ? '1 alteração não salva' : `${quantidade} alterações não salvas`
);

const JanelaFormModal = ({ show, mode, janela, loading, erroApi, onHide, onSubmit }) => {
  const edicao = mode === 'edit';
  const original = useMemo(() => (janela ? paraRascunho(janela) : rascunhoVazio()), [janela]);
  const [rascunho, setRascunho] = useState(original);
  // Conta as tentativas de salvar: cada uma com erro rola o corpo até o alerta
  const [tentativas, setTentativas] = useState(0);
  const alertaRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setRascunho(original);
    setTentativas(0);
  }, [original, show]);

  const { destino, carregando, erro: erroDestino } = useJanelaDestino({
    tipo: rascunho.tipo,
    cnpjRaiz: rascunho.cnpjRaiz,
    cnpj: rascunho.cnpj,
    ignorarId: edicao ? janela?.id : undefined
  });

  // Mesmas regras da API: validação do rascunho + destino duplicado (só com a resposta do /destino em mãos;
  // com a consulta pendente a API continua sendo a guarda final)
  const erros = useMemo(() => {
    const lista = validarJanela(rascunho);
    if (destino?.duplicada) lista.push(`Já existe a janela "${destino.duplicada.nome}" para este destino.`);
    return lista;
  }, [rascunho, destino]);
  const pendencias = useMemo(
    () => (edicao ? diferencas(original, rascunho).length : 0),
    [edicao, original, rascunho]
  );

  // Criação compara com o rascunho vazio; edição, com a janela carregada
  const temAlteracoes = useMemo(
    () => diferencas(original, rascunho).length > 0,
    [original, rascunho]
  );

  const handleFechar = () => {
    if (loading) return;
    if (temAlteracoes && !window.confirm('Descartar as alterações não salvas?')) return;
    onHide();
  };

  const alterar = (campos) => setRascunho((atual) => ({ ...atual, ...campos }));

  const handleSalvar = () => {
    setTentativas((atual) => atual + 1);
    if (erros.length) return;
    onSubmit(paraPayload(rascunho));
  };

  const mensagens = tentativas > 0 ? erros : [];
  const errosNaTentativa = tentativas > 0 && erros.length > 0;

  // O alerta fica no topo do corpo rolável e o Salvar no cabeçalho fixo: traz o alerta para a vista
  useEffect(() => {
    if (!errosNaTentativa && !erroApi) return;
    alertaRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    // erros muda a cada edição; só uma nova tentativa ou um novo erro da API deve rolar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tentativas, erroApi]);

  return (
    <Modal
      show={show}
      onHide={handleFechar}
      size="xl"
      centered
      scrollable
      backdrop="static"
      keyboard={!loading}
    >
      <Modal.Header className="flex-column align-items-stretch gap-2">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <Modal.Title as="h5">{edicao ? 'Editar janela' : 'Nova janela'}</Modal.Title>
          <div className="d-flex align-items-center gap-2">
            {pendencias > 0 ? (
              <span className="small text-warning-emphasis">{textoPendencias(pendencias)}</span>
            ) : null}
            <Button variant="outline-secondary" size="sm" onClick={handleFechar} disabled={loading}>
              Cancelar
            </Button>
            <ButtonWithLoading variant="primary" size="sm" onClick={handleSalvar} loading={loading || undefined}>
              Salvar janela
            </ButtonWithLoading>
          </div>
        </div>
        <div className="d-flex flex-wrap align-items-end gap-3">
          <Form.Group controlId="janela-nome" className="flex-grow-1">
            <Form.Label className="small mb-1">Nome da janela</Form.Label>
            <Form.Control
              value={rascunho.nome}
              maxLength={MAX_NOME}
              placeholder="Ex.: Havan — matriz e filiais"
              onChange={(event) => alterar({ nome: event.target.value })}
            />
          </Form.Group>
          <Badge bg="light" text="dark" className="border fw-normal py-2">
            {destinoCompleto(rascunho) ? textoDestino(rascunho) : 'Destino não definido'}
          </Badge>
        </div>
      </Modal.Header>

      <Modal.Body>
        {erroApi || mensagens.length ? (
          <Alert variant="danger" ref={alertaRef}>
            {erroApi ? <div>{erroApi}</div> : null}
            {mensagens.length ? (
              <ul className="mb-0 ps-3">
                {mensagens.map((mensagem) => <li key={mensagem}>{mensagem}</li>)}
              </ul>
            ) : null}
          </Alert>
        ) : null}

        <JanelaDestinoSection
          rascunho={rascunho}
          destino={destino}
          carregando={carregando}
          erro={erroDestino}
          onChange={alterar}
        />
        <JanelaTurnosSection dias={rascunho.dias} onChange={(dias) => alterar({ dias })} />

        <fieldset className="mb-4">
          <legend className="h6 mb-2">Chegada fora da janela</legend>
          {MODOS_FORA_JANELA.map((modo) => (
            <div key={modo.valor} className="mb-2">
              <Form.Check
                type="radio"
                name="janela-modo-fora"
                id={`janela-modo-${modo.valor}`}
                label={modo.texto}
                checked={rascunho.modoForaJanela === modo.valor}
                aria-describedby={`janela-modo-${modo.valor}-ajuda`}
                onChange={() => alterar({ modoForaJanela: modo.valor })}
              />
              <div id={`janela-modo-${modo.valor}-ajuda`} className="small text-muted ms-4">{modo.ajuda}</div>
            </div>
          ))}
        </fieldset>

        <JanelaSimuladorSection rascunho={rascunho} />
      </Modal.Body>
    </Modal>
  );
};

JanelaFormModal.propTypes = {
  show: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  janela: PropTypes.object,
  loading: PropTypes.bool,
  erroApi: PropTypes.string,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

JanelaFormModal.defaultProps = {
  janela: null,
  loading: false,
  erroApi: null
};

export default JanelaFormModal;
